import { createReducer, on } from '@ngrx/store';

import { RequestStatus } from '../../enums/request-status.enum';
import {
  LinkTemplate,
  LinkTemplateDetail,
  LinkTemplatesListMeta,
  LinkTemplatesListParams,
} from '../models/link-template.model';
import * as LinkTemplatesActions from './link-templates.actions';

export interface LinkTemplatesState {
  templates: LinkTemplate[];
  meta: LinkTemplatesListMeta | null;
  currentTemplate: LinkTemplateDetail | null;
  listStatus: RequestStatus;
  detailStatus: RequestStatus;
  saveStatus: RequestStatus;
  deleteStatus: RequestStatus;
  defaultStatus: RequestStatus;
  exportStatus: RequestStatus;
  exportMessage: string | null;
  error: string | null;
  lastQuery: LinkTemplatesListParams;
}

const initialState: LinkTemplatesState = {
  templates: [],
  meta: null,
  currentTemplate: null,
  listStatus: RequestStatus.Idle,
  detailStatus: RequestStatus.Idle,
  saveStatus: RequestStatus.Idle,
  deleteStatus: RequestStatus.Idle,
  defaultStatus: RequestStatus.Idle,
  exportStatus: RequestStatus.Idle,
  exportMessage: null,
  error: null,
  lastQuery: {},
};

export const linkTemplatesReducer = createReducer(
  initialState,
  on(LinkTemplatesActions.loadTemplates, (state, query) => ({
    ...state,
    listStatus: RequestStatus.Loading,
    error: null,
    lastQuery: query,
  })),
  on(LinkTemplatesActions.loadTemplatesSuccess, (state, { templates, meta }) => ({
    ...state,
    templates,
    meta,
    listStatus: RequestStatus.Success,
  })),
  on(LinkTemplatesActions.loadTemplatesFailure, (state, { error }) => ({
    ...state,
    listStatus: RequestStatus.Error,
    error,
  })),
  on(LinkTemplatesActions.loadTemplate, (state) => ({
    ...state,
    currentTemplate: null,
    detailStatus: RequestStatus.Loading,
    error: null,
  })),
  on(LinkTemplatesActions.loadTemplateSuccess, (state, { template }) => ({
    ...state,
    currentTemplate: template,
    detailStatus: RequestStatus.Success,
  })),
  on(LinkTemplatesActions.loadTemplateFailure, (state, { error }) => ({
    ...state,
    detailStatus: RequestStatus.Error,
    error,
  })),
  on(LinkTemplatesActions.createTemplate, LinkTemplatesActions.updateTemplate, (state) => ({
    ...state,
    saveStatus: RequestStatus.Loading,
    error: null,
  })),
  on(LinkTemplatesActions.createTemplateSuccess, LinkTemplatesActions.updateTemplateSuccess, (state, { template }) => ({
    ...state,
    saveStatus: RequestStatus.Success,
    currentTemplate: template,
  })),
  on(LinkTemplatesActions.deleteTemplate, (state) => ({
    ...state,
    deleteStatus: RequestStatus.Loading,
    error: null,
  })),
  on(LinkTemplatesActions.deleteTemplateSuccess, (state) => ({
    ...state,
    deleteStatus: RequestStatus.Success,
  })),
  on(LinkTemplatesActions.setDefaultTemplate, (state) => ({
    ...state,
    defaultStatus: RequestStatus.Loading,
    error: null,
  })),
  on(LinkTemplatesActions.setDefaultTemplateSuccess, (state) => ({
    ...state,
    defaultStatus: RequestStatus.Success,
  })),
  on(LinkTemplatesActions.mutationFailure, (state, { error }) => ({
    ...state,
    saveStatus: state.saveStatus === RequestStatus.Loading ? RequestStatus.Error : state.saveStatus,
    deleteStatus: state.deleteStatus === RequestStatus.Loading ? RequestStatus.Error : state.deleteStatus,
    defaultStatus: state.defaultStatus === RequestStatus.Loading ? RequestStatus.Error : state.defaultStatus,
    error,
  })),
  on(LinkTemplatesActions.exportTemplates, (state) => ({
    ...state,
    exportStatus: RequestStatus.Loading,
    exportMessage: null,
    error: null,
  })),
  on(LinkTemplatesActions.exportTemplatesSuccess, (state, { message }) => ({
    ...state,
    exportStatus: RequestStatus.Success,
    exportMessage: message,
  })),
  on(LinkTemplatesActions.exportTemplatesFailure, (state, { error }) => ({
    ...state,
    exportStatus: RequestStatus.Error,
    exportMessage: null,
    error,
  })),
  on(LinkTemplatesActions.resetCurrentTemplate, (state) => ({
    ...state,
    currentTemplate: null,
    detailStatus: RequestStatus.Idle,
  })),
  on(LinkTemplatesActions.resetTemplatesState, () => initialState)
);
